import { delay } from '@fox/atoms';
import React, { useRef, useState } from 'react';
import { PaymentAddComponentProps } from '../../../contracts';
import { CardForm, twoDigitCardYear } from '../../card-helpers';

function isNabPublicSettings(settings: unknown): settings is { BaseUrl: string; Merchant: string } {
	return settings && typeof settings === 'object' && typeof settings['BaseUrl'] === 'string' && typeof settings['Merchant'] === 'string';
}

/* 
This component is a little weird - mainly due to the POST redirect style submission for saving a card.
We need to do a form post which has hidden fields to store the required data.
*/
export function NabTransactAddCreditCard({
	completeIntent,
	intentData,
	onSuccess,
	settings,
	onCancel,
	showSave
}: PaymentAddComponentProps) {
	const intent = intentData as {
		key?: string | null;
		error?: string | null;
		intentId?: string | null;
		data?: { timestamp: string; fingerprint?: string | null; returnUrl: string };
	};
	const [error, setError] = useState<string[]>([]);
	const targetId = useRef('transact-card'); // nanoid());
	const formRef = useRef<HTMLFormElement>(null);

	const cardRef = useRef<HTMLInputElement>(null);
	const monthRef = useRef<HTMLInputElement>(null);
	const yearRef = useRef<HTMLInputElement>(null);
	const loadCompletionRef = useRef<((value: unknown) => void) | null>(null);

	const data = useRef<{ timeout: any; last4: string; expires: { month?: string; year?: string } }>({
		timeout: null,
		last4: '',
		expires: {}
	});
	if (!isNabPublicSettings(settings)) return <>Invalid configuration</>;
	if (!intent?.data?.fingerprint || !intent.intentId) return null;

	async function checkCard() {
		if (!intent?.intentId) return;

		const result = await completeIntent(intent.intentId, JSON.stringify({ last4: data.current.last4, expires: data.current.expires }));
		if (result === null || result === undefined) {
			setError(['Failed to create NAB Customer']);
			return;
		}
		onSuccess(result);
	}

	async function loaded() {
		if (!data.current.last4) return;

		clearTimeout(data.current.timeout);

		try {
			// artificial delay before calling the card check
			await delay(250);
			await checkCard();
		} catch (e) {
			console.log(e);
			setError(['Failed to create customer in NAB']);
		}

		// at this point if the promise still exists, we should resolve it, so the form submission returns
		if (loadCompletionRef.current) loadCompletionRef.current(null);
	}

	return (
		<>
			<CardForm
				onCancel={onCancel}
				apiErrors={error}
				submitFunc={vals => {
					// validation is handled by the create form
					if (!cardRef.current || !monthRef.current || !yearRef.current) {
						setError(['Failed to submit card details to NAB']);
						return;
					}

					data.current.expires = vals.expires;
					data.current.last4 = vals.cardNumber.substring(vals.cardNumber.length - 4);
					// manually update the hidden refs so we can submit the form
					cardRef.current.value = vals.cardNumber;
					yearRef.current.value = twoDigitCardYear(Number(vals.expires.year));
					monthRef.current.value = vals.expires.month;

					// The button can be a submit button, however it seemed to break the form submission if I set loading true
					// I know a event handler will perform a sync flush, perhaps setting loading breaks the connection of the button to the form resulting in it not actually submitting
					// given that, I am explicitly submitting the form here
					formRef.current?.submit();

					return new Promise((resolve, reject) => {
						loadCompletionRef.current = resolve;
						// this form should load should happen instantly, but we try to check the card after 10 seconds just in case the onload event fails
						data.current.timeout = setTimeout(async () => {
							await loaded();
							resolve(null);
						}, 10000);
					});
				}}
				showSave={showSave}
			/>
			{/* This form is only to submit directly to NAB */}
			<form
				target={targetId.current}
				ref={formRef}
				method="POST"
				action={new URL('/live/directpost/crnmanage', settings.BaseUrl).href}
			>
				<input type="hidden" value={intent.data.timestamp} name="EPS_TIMESTAMP" />
				<input type="hidden" value={intent.data.fingerprint} name="EPS_FINGERPRINT" />
				<input type="hidden" value={settings.Merchant} name="EPS_MERCHANT" />
				<input type="hidden" value={'CRN'} name="EPS_TYPE" />
				<input type="hidden" value={'ADDCRN'} name="EPS_ACTION" />
				<input type="hidden" value={intent.data.returnUrl} name="EPS_RESULTURL" />

				<input type="hidden" ref={yearRef} name="EPS_EXPIRYYEAR" />
				<input type="hidden" ref={cardRef} name="EPS_CARDNUMBER" />
				<input type="hidden" ref={monthRef} name="EPS_EXPIRYMONTH" />
				<input type="hidden" value={intent.intentId} name="EPS_CRN" />
			</form>
			{/* Target for the POST, we use the loaded trigger as confirmation the process is done. If successful the complete intention will work. Otherwise it will show an error */}
			<iframe key={targetId.current} style={{ display: 'none' }} name={targetId.current} title="Card creation" onLoad={loaded} />
		</>
	);
}
